import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Search, MapPin, ClipboardCheck, FileText, ShieldCheck, Clock } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CategoryIcon, SectionHeader, LicenceBadge } from "@/components/shared";
import { CompanyCard } from "@/components/CompanyCard";

const HERO_IMG = "https://images.pexels.com/photos/17285751/pexels-photo-17285751.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const LOCATIONS = ["Dubai Marina", "Downtown Dubai", "Dubai Creek Harbour", "Business Bay", "Palm Jumeirah", "Dubai Hills Estate"];

const FloatCard = ({ icon: Icon, title, text, className, delay }) => (
  <div className={`absolute bg-white rounded-xl shadow-soft border border-[#E3E8E8] p-3.5 max-w-[230px] fade-up ${className}`} style={{ animationDelay: delay }}>
    <div className="flex gap-3">
      <div className="w-9 h-9 rounded-lg bg-[#E8F5F1] flex items-center justify-center shrink-0">
        <Icon size={18} className="text-[#007F83]" />
      </div>
      <div>
        <p className="font-display font-bold text-sm text-[#0B1733] leading-tight">{title}</p>
        <p className="text-xs text-[#4A5568] mt-0.5 leading-snug">{text}</p>
      </div>
    </div>
  </div>
);

export default function HomePage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("Dubai");
  const [cats, setCats] = useState([]);
  const [posts, setPosts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    api.get("/categories?featured=true").then((r) => setCats(r.data.slice(0, 5))).catch(() => {});
    api.get("/community/posts?limit=2").then((r) => setPosts(r.data.items)).catch(() => {});
    api.get("/companies/featured").then((r) => setCompanies(r.data.slice(0, 3))).catch(() => {});
    api.get("/properties?limit=3").then((r) => setProperties(r.data.items)).catch(() => {});
  }, []);

  const doSearch = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (loc && loc !== "Dubai") params.set("location", loc);
    navigate(`/companies?${params.toString()}`);
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative">
        <div className="propora-container pt-12 lg:pt-16 pb-6">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="fade-up">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#4A5568] mb-5">Dubai's property network</p>
              <h1 className="font-display font-extrabold tracking-tight text-[#0B1733] text-5xl sm:text-6xl leading-[1.02]">
                Your property.<br />Your people.<br />One place.
              </h1>
              <p className="mt-6 text-lg text-[#4A5568] max-w-md">
                Connect with owners, discover specialists and get your property projects done.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/post-a-job" data-testid="hero-post-job-btn">
                  <Button className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white px-7 h-12 text-base">
                    Post a job <ArrowRight size={18} className="ml-1.5" />
                  </Button>
                </Link>
                <Link to="/community" data-testid="hero-community-btn">
                  <Button variant="outline" className="rounded-full border-[#E3E8E8] text-[#0B1733] hover:bg-[#E8F5F1] hover:text-[#007F83] px-7 h-12 text-base">
                    Explore the community
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative h-[360px] lg:h-[460px]">
              <div className="absolute inset-0 rounded-3xl overflow-hidden shadow-soft">
                <img src={HERO_IMG} alt="Dubai waterfront architecture" className="w-full h-full object-cover" />
              </div>
              <FloatCard icon={ClipboardCheck} title="Snagging inspection" text="Find qualified inspectors for your handover." className="top-6 left-2 lg:-left-8" delay="0.15s" />
              <FloatCard icon={FileText} title="Compare quotes" text="Post your project and get responses from listed companies." className="top-28 right-2 lg:-right-6" delay="0.3s" />
              <FloatCard icon={ShieldCheck} title="Trade licence checked" text="We check each company's trade licence before it goes live." className="bottom-6 left-6 lg:left-12" delay="0.45s" />
            </div>
          </div>
        </div>

        {/* SEARCH BAR */}
        <div className="propora-container -mb-8 relative z-10">
          <form onSubmit={doSearch} className="bg-white rounded-2xl shadow-soft border border-[#E3E8E8] p-3 flex flex-col md:flex-row gap-3" data-testid="hero-search-form">
            <div className="flex-1 flex items-center gap-2 px-3">
              <Search size={20} className="text-[#007F83] shrink-0" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="What do you need help with?" data-testid="search-keyword-input" className="border-0 shadow-none focus-visible:ring-0 text-base px-0 h-12 text-[#0B1733] placeholder:text-[#5A6673] placeholder:font-medium" />
            </div>
            <div className="md:w-56 flex items-center gap-2 px-3 border-t md:border-t-0 md:border-l border-[#E3E8E8] pt-3 md:pt-0">
              <MapPin size={18} className="text-[#007F83] shrink-0" />
              <Select value={loc} onValueChange={setLoc}>
                <SelectTrigger className="border-0 shadow-none focus:ring-0 text-[#0B1733] font-medium" data-testid="search-location-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Dubai">Dubai (all areas)</SelectItem>
                  {LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" data-testid="find-specialists-btn" className="rounded-xl bg-[#007F83] hover:bg-[#006B70] text-white h-12 px-7">
              Find specialists <ArrowRight size={18} className="ml-1.5" />
            </Button>
          </form>
        </div>
      </section>

      {/* CATEGORY ROW */}
      <section className="propora-container pt-20 pb-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="category-row">
          {cats.map((c) => (
            <Link key={c.slug} to={`/companies?service=${c.slug}`} data-testid={`category-${c.slug}`}
              className="bg-white rounded-xl border border-[#E3E8E8] p-4 flex flex-col items-center gap-2.5 card-lift text-center">
              <CategoryIcon name={c.icon} size={24} className="text-[#007F83]" />
              <span className="text-sm font-semibold text-[#0B1733] leading-tight">{c.name.split(" & ")[0].split(" (")[0]}</span>
            </Link>
          ))}
          <Link to="/companies" data-testid="category-view-all" className="bg-[#0B1733] rounded-xl p-4 flex flex-col items-center gap-2.5 card-lift text-center">
            <CategoryIcon name="grid" size={24} className="text-white" />
            <span className="text-sm font-semibold text-white">View all</span>
          </Link>
        </div>
      </section>

      {/* COMMUNITY + COMPANIES */}
      <section className="propora-container py-16 grid lg:grid-cols-2 gap-12">
        <div>
          <SectionHeader eyebrow="Community" title="Conversations that matter" action="View all discussions" actionTo="/community" />
          <div className="space-y-4">
            {posts.map((p) => (
              <Link key={p.id} to={`/community/posts/${p.id}`} data-testid={`home-post-${p.id}`}
                className="block bg-white rounded-2xl border border-[#E3E8E8] p-5 card-lift">
                <h3 className="font-display font-bold text-lg text-[#0B1733]">{p.title}</h3>
                <p className="mt-1.5 text-sm text-[#4A5568] line-clamp-2">{p.body}</p>
                <div className="mt-3 flex items-center gap-3 text-xs text-[#4A5568]">
                  <span className="font-semibold text-[#0B1733]">{p.author}</span>
                  <span>· {p.reply_count} replies</span>
                  {p.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {p.location}</span>}
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div>
          <SectionHeader eyebrow="Directory" title="Find the right company" action="View all companies" actionTo="/companies" />
          <div className="space-y-4">
            {companies.map((c) => <CompanyCard key={c.id} company={c} />)}
          </div>
        </div>
      </section>

      {/* JOB CTA */}
      <section className="propora-container">
        <div className="bg-[#E8F5F1] rounded-3xl p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6" data-testid="job-cta-band">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shrink-0">
              <FileText size={26} className="text-[#007F83]" />
            </div>
            <div>
              <h3 className="font-display font-extrabold text-2xl text-[#0B1733]">Have a property project?</h3>
              <p className="text-[#4A5568] mt-1">Tell us what you need. Compare responses from listed companies.</p>
            </div>
          </div>
          <Link to="/post-a-job" data-testid="cta-post-job-btn" className="shrink-0">
            <Button className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white h-12 px-7 text-base">
              Post a job — free <ArrowRight size={18} className="ml-1.5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* PROPERTIES */}
      <section className="propora-container py-16">
        <SectionHeader eyebrow="Discover" title="Curated properties & projects" action="Browse all" actionTo="/properties" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {properties.map((p) => (
            <Link key={p.id} to={`/properties/${p.slug}`} data-testid={`home-property-${p.slug}`} className="bg-white rounded-2xl border border-[#E3E8E8] overflow-hidden card-lift">
              <div className="h-48 bg-[#E8F5F1]"><img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" /></div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-[#007F83] bg-[#E8F5F1] px-2 py-0.5 rounded">{p.status}</span>
                  <span className="text-[11px] text-[#4A5568]">{p.property_type}</span>
                </div>
                <h3 className="font-display font-bold text-lg text-[#0B1733]">{p.name}</h3>
                <p className="text-sm text-[#4A5568] inline-flex items-center gap-1 mt-1"><MapPin size={13} /> {p.location}</p>
                <p className="mt-3 font-display font-extrabold text-[#0B1733]">{p.asking_price}</p>
              </div>
            </Link>
          ))}
        </div>
        <p className="text-right text-xs text-[#4A5568] mt-4">Concept preview · Example content</p>
      </section>
    </div>
  );
}
